/**
 * @class MCBDialogElement
 * @classdesc A dialog element that can be opened and closed.
 * @extends HTMLElement
 */
class MCBDialogElement extends HTMLElement {
  constructor() {
    super();
    
    if (this.shadowDomTemplate) {
      const shadowDomElement = document.getElementById(this.shadowDomTemplate);
      this.attachShadow({ mode: 'open' }).appendChild(shadowDomElement.content.cloneNode(true));
    }
  }


  /** 
   * Observe attributes 
   */
  static get observedAttributes() {
    return ['open'];
  }
  

  /**
   * Runs when the element is added to the DOM
   */
  connectedCallback() {
    this.setAttribute('role', 'dialog');
        
    /** Get additional controls from shadow template if relevant */
    if (this.shadowDomTemplate) {
      const overlay = this.getShadowPartByName('overlay');
      overlay?.addEventListener('click', this.hide.bind(this));
      
      const closeButton = this.getShadowPartByName('close-button tap-area');
      closeButton?.addEventListener('click', this.hide.bind(this));
    }
    
    document.addEventListener('click', this.handleOutsideClick.bind(this));
        
        
    /** Init all controls */
    this.controls.forEach(control => {
      control.addEventListener('click', this.handleControlClick.bind(this));
    });
  }


  /**
   * Runs when the element is removed from the DOM 
   */
  disconnectedCallback() {    
    document.removeEventListener('click', this.handleOutsideClick.bind(this));
    
    this.controls.forEach(control => {
      control.removeEventListener('click', this.handleControlClick.bind(this));
    });
  }
  
  lockScroll() {
    if (document.body.classList.contains('overflow-hidden')) {
      document.body.classList.remove('overflow-hidden');
    } else {
      document.body.classList.add('overflow-hidden');
    }
  }
  
  
  /**
   * Hide when clicking outside
   * @param {Event} event 
   * @returns void
   */
  handleOutsideClick(event) {
    if (this.open && !this.contains(event.target) && !this.controls.includes(event.target)) {
      this.hide();
    }
  }


  /**
   * Handle the click event on the control
   */
  handleControlClick() {
    this.open ? this.hide() : this.show();
  }


  /**
   * Set 'open' attribute, which is observed
   */
  show() {
    this.setAttribute('open', '');
    document.body.classList.add('mcb-dialog-open');
    
    if (this.isSmallScreen.matches) {
      this.lockScroll();
    }
  }
  
  
  /**
   * Remove 'open' attribute, which is observed
   */
  hide() {
    this.removeAttribute('open');
    document.body.classList.remove('mcb-dialog-open');
    
    if (this.isSmallScreen.matches) {
      this.lockScroll();
    }
  }
  
  
  /**
   * Get the shadow DOM template ID if relevant
   * @returns {string}
   */
  get shadowDomTemplate() {
    return this.getAttribute('template');
  }


  /**
   * Get all open and close buttons
   * @returns {HTMLElement[]}
   */
  get controls() {
    return [...document.querySelectorAll(`[aria-controls='${this.id}']`)];
  }


  /**
   * Return whether the dialog is open
   * @returns {boolean}
   */
  get open() {
    return this.hasAttribute('open');
  }
  
  /**
   * Match media breakpoint
   */
  get isSmallScreen() {
    return window.matchMedia("(max-width: 999px)");
  }


  /**
   * Get a shadow part by name
   * @param {string} name - The name of the shadow part
   */
  getShadowPartByName(name) {
    return this.shadowRoot?.querySelector(`[part='${name}']`);
  }


  /** 
    Runs whenever any `observedAttributes` change.
    This is how to open and close.
    # ['open']
  */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      if (oldValue === null && newValue === '') {
        this.style.display = 'block';
        document.body.classList.add('mcb-dialog-open');
        this.animateEnter();
      } else if (oldValue !== null && newValue === null) {
        const hideTransitionPromise = this.animateLeave().finished;
        
        hideTransitionPromise.then(() => {
          this.style.display = 'none';
          document.body.classList.remove('mcb-dialog-open');
        });
      }
    }
  }

  animateEnter() {
    return this.animate({ opacity: [0, 1] }, { duration: 200 });
  }

  animateLeave() {
    return this.animate({ opacity: [1, 0] }, { duration: 200 });
  }
}

if (!window.customElements.get('mcb-dialog-element')) {
  window.customElements.define('mcb-dialog-element', MCBDialogElement);
}

export default MCBDialogElement;
